import React, { useState } from "react";
// @ts-ignore
import "./FaqSection.css";

export interface HomeFAQItem {
  question: string;
  answer: string;
}

// Every answer restates something already published elsewhere on the site
// (quote flow, Policies, Terms, fleet/booking sections) — no invented rates,
// hours or policies.
export const homeFaqs: HomeFAQItem[] = [
  {
    question: "How does GS Limo Elite pricing work?",
    answer:
      "Pricing depends on your route, vehicle and travel time. Share your pickup and drop-off details and we confirm the rate before you book, so the quote covers the agreed car, route and timing — with nothing added after the ride.",
  },
  {
    question: "How do I book an airport pickup?",
    answer:
      "Call (929) 452-6031 or send the booking form with your flight number, pickup location and destination. We plan the pickup around your departure time, recommend a vehicle for your group, and confirm the reservation details before you travel.",
  },
  {
    question: "What happens if my flight is delayed?",
    answer:
      "Include your flight number with the booking. GS Limo Elite may monitor flight status when flight information is provided, and if your travel plans or flight information change, contact us and we adjust the pickup around your new arrival.",
  },
  {
    question: "How many passengers and how much luggage can you carry?",
    answer:
      "Seating capacity is shared on request, because it depends on the vehicle. Tell us your group size and luggage count and we recommend a sedan, SUV or Sprinter van — or coordinate more than one vehicle for larger parties.",
  },
  {
    question: "Where do we meet the driver at the airport?",
    answer:
      "Pickup instructions for your terminal are confirmed with your reservation, and on the day of travel we ask passengers to follow the airport's current pickup rules along with those details. Your driver helps you and your luggage get settled.",
  },
  {
    question: "Do you provide child car seats?",
    answer:
      "GS Limo Elite does not provide child car seats. Passengers must bring and install their own, so let us know in advance if your group needs the space.",
  },
  {
    question: "Can I cancel or reschedule a booking?",
    answer:
      "Bookings are confirmed once your trip details and payment arrangements are agreed with our team. Cancellation and rescheduling terms are provided at the time of booking, so you have them in writing before you commit.",
  },
  {
    question: "Do you provide Philadelphia Airport (PHL) car service from Glassboro?",
    answer:
      "Yes. We regularly provide private car and limo service between Glassboro, South Jersey and Philadelphia International Airport (PHL). Share your flight details and destination when requesting a quote.",
  },
  {
    question: "Do you serve Newark Airport (EWR) and JFK?",
    answer:
      "Yes. GS Limo Elite provides airport transfers to and from Newark Liberty International Airport (EWR), JFK, LaGuardia (LGA), Atlantic City (ACY) and Albany (ALB), in addition to PHL.",
  },
  {
    question: "Which areas do you serve?",
    answer:
      "We are based in Glassboro, NJ and serve New Jersey, New York and Pennsylvania, with airport pickups and drop-offs at PHL, EWR, JFK, LGA, ACY and ALB, provided your destination is within our service area.",
  },
];

export default function FaqSection(): JSX.Element {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section className="home-faq-section py-5">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-9">
            <div className="text-center mb-4">
              <span className="section-badge">FAQ</span>
              <h2 className="section-title">Limo &amp; Airport Pickup Questions</h2>
              <p className="home-faq-lead">
                Straight answers on pricing, airport pickups, luggage and
                bookings. Anything not covered here is confirmed with your
                quote.
              </p>
            </div>
            <div
              className="gs-faq-accordion"
              role="region"
              aria-label="Limo service frequently asked questions"
            >
              {homeFaqs.map((faq, idx) => {
                const isOpen = openIndex === idx;
                return (
                  <div className="gs-faq-item" key={idx}>
                    <button
                      type="button"
                      className="gs-faq-question"
                      aria-expanded={isOpen}
                      aria-controls={`home-faq-${idx}`}
                      onClick={() => setOpenIndex(isOpen ? null : idx)}
                    >
                      <span>{faq.question}</span>
                      <i
                        className={`fas fa-chevron-down${isOpen ? " open" : ""}`}
                        aria-hidden="true"
                      ></i>
                    </button>
                    <div
                      id={`home-faq-${idx}`}
                      className={`gs-faq-answer${isOpen ? " open" : ""}`}
                      aria-hidden={!isOpen}
                    >
                      <p>{faq.answer}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
